import React, { useState, useEffect, useRef } from 'react';
import { Save, X, Plus, Award, BarChart3, TrendingUp, DollarSign, Calendar, Star, Edit2, Trash2, Upload, Sparkles, Loader2, FileUp, ChevronDown, ChevronUp, Building2, FileText, Users, CheckCircle, Shield, Lightbulb, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { DocumentAnalyzer, ExtractionResult, ExtractedPastPerformance } from '../../lib/document-analysis';

// Structured deliverable type
interface StructuredDeliverable {
  name: string;
  description: string;
  quantity?: number;
  status?: 'Completed' | 'In Progress' | 'Planned';
}

// Supporting document reference
interface SupportingDocument {
  name: string;
  type: string;
  url?: string;
  description?: string;
}

// Contracting office address
interface ContractingOfficeAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

// Subcontracting plan details
interface SubcontractingPlan {
  goals?: string;
  achievements?: string;
  partners?: string[];
}

// Helper functions to convert between numeric (1-5) and text performance ratings
// Database stores: 'exceptional', 'satisfactory', 'marginal', 'unsatisfactory'
const numericToTextRating = (rating: number): string => {
  if (rating >= 5) return 'exceptional';
  if (rating >= 4) return 'exceptional'; // No "very good" option, map to exceptional
  if (rating >= 3) return 'satisfactory';
  if (rating >= 2) return 'marginal';
  return 'unsatisfactory';
};

const textToNumericRating = (rating: string | null): number => {
  if (!rating) return 5; // Default to exceptional
  const normalized = rating.toLowerCase();
  if (normalized === 'exceptional') return 5;
  if (normalized === 'satisfactory') return 3;
  if (normalized === 'marginal') return 2;
  if (normalized === 'unsatisfactory') return 1;
  return 5; // Default
};

interface PerformanceRecord {
  id?: string;

  // === CONTRACT IDENTIFICATION ===
  projectName: string;
  contractNumber: string;
  sectorType: 'Government' | 'Private Sector';
  agency: string;
  subAgency: string;
  contractingOfficeAddress: ContractingOfficeAddress;
  naicsCode: string;
  pscCode: string;
  contractType: 'Prime' | 'Subcontractor' | 'JV';
  rolePercentage: number; // Percentage self-performed (0-100)

  // === CONTRACT VALUE & PERIOD ===
  contractValue: number;
  ceilingValue: number;
  startDate: string;
  endDate: string;
  optionYears: number;
  popType: 'Virtual' | 'On-site' | 'Hybrid' | '';
  popLocation: string;

  // === CONTRACT VEHICLE ===
  contractVehicle: string; // GSA Schedule, GWAC, BPA, IDIQ, Standalone
  vehicleNumber: string;
  taskOrderNumber: string;

  // === SCOPE OF WORK ===
  scopeDescription: string;
  projectObjectives: string[];
  deliverables: string; // Legacy single text field
  structuredDeliverables: StructuredDeliverable[];
  serviceAreas: string[];

  // === TECHNICAL APPROACH ===
  technicalApproach: string;
  technologiesUsed: string[];
  complianceStandards: string[]; // Section 508, FedRAMP, FISMA, etc.
  securityRequirements: string;
  qualityStandards: string[];

  // === KEY ACCOMPLISHMENTS ===
  keyAccomplishments: string[];
  innovations: string[];

  // === PERFORMANCE METRICS ===
  performanceRating: number; // 1-5 stars (internal)
  clientSatisfaction: number; // 1-10 score
  onTimeDelivery: boolean;
  withinBudget: boolean;

  // === CPARS RATINGS ===
  cparsRecordId: string;
  cparsQualityRating: number | null;
  cparsScheduleRating: number | null;
  cparsCostRating: number | null;
  cparsManagementRating: number | null;
  cparsSmallBusinessRating: number | null;
  cparsOverallRating: number | null;
  cparsNarrative: string;

  // === TEAM & RESOURCES ===
  teamSize: number;
  fteCount: number;
  projectComplexity: number; // 1-10 scale
  revenueImpact: number;

  // === CLIENT REFERENCE ===
  clientReference: {
    name: string;
    title: string;
    email: string;
    phone: string;
    organization?: string;
  };

  // === RELEVANCE & REUSE ===
  demonstratedCapabilities: string[];
  applicableNaics: string[];
  targetAgencies: string[];
  relevanceTags: string[];

  // === COMPLIANCE ===
  farClausesComplied: string[];
  subcontractingPlan: SubcontractingPlan;
  limitationsCompliance: string;

  // === LESSONS LEARNED ===
  lessonsLearned: string[];
  whatWorkedWell: string[];
  areasForImprovement: string[];

  // === SUPPORTING DOCUMENTS ===
  documentUrl: string; // Legacy
  supportingDocuments: SupportingDocument[];
  cparsDocumentUrl: string;
  contractDocumentUrl: string;

  // === VERIFICATION ===
  verified: boolean;
  verificationDate: string;
  verificationSource: string;
}

export function PastPerformance() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [performances, setPerformances] = useState<PerformanceRecord[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Document import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [extractedProjects, setExtractedProjects] = useState<ExtractedPastPerformance[]>([]);
  const [selectedImports, setSelectedImports] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Default empty record state
  const getEmptyRecord = (): PerformanceRecord => ({
    // Contract Identification
    projectName: '',
    contractNumber: '',
    sectorType: 'Government',
    agency: '',
    subAgency: '',
    contractingOfficeAddress: {},
    naicsCode: '',
    pscCode: '',
    contractType: 'Prime',
    rolePercentage: 100,

    // Contract Value & Period
    contractValue: 0,
    ceilingValue: 0,
    startDate: '',
    endDate: '',
    optionYears: 0,
    popType: '',
    popLocation: '',

    // Contract Vehicle
    contractVehicle: '',
    vehicleNumber: '',
    taskOrderNumber: '',

    // Scope of Work
    scopeDescription: '',
    projectObjectives: [],
    deliverables: '',
    structuredDeliverables: [],
    serviceAreas: [],

    // Technical Approach
    technicalApproach: '',
    technologiesUsed: [],
    complianceStandards: [],
    securityRequirements: '',
    qualityStandards: [],

    // Key Accomplishments
    keyAccomplishments: [],
    innovations: [],

    // Performance Metrics
    performanceRating: 5,
    clientSatisfaction: 10,
    onTimeDelivery: true,
    withinBudget: true,

    // CPARS Ratings
    cparsRecordId: '',
    cparsQualityRating: null,
    cparsScheduleRating: null,
    cparsCostRating: null,
    cparsManagementRating: null,
    cparsSmallBusinessRating: null,
    cparsOverallRating: null,
    cparsNarrative: '',

    // Team & Resources
    teamSize: 1,
    fteCount: 0,
    projectComplexity: 5,
    revenueImpact: 0,

    // Client Reference
    clientReference: { name: '', title: '', email: '', phone: '', organization: '' },

    // Relevance & Reuse
    demonstratedCapabilities: [],
    applicableNaics: [],
    targetAgencies: [],
    relevanceTags: [],

    // Compliance
    farClausesComplied: [],
    subcontractingPlan: {},
    limitationsCompliance: '',

    // Lessons Learned
    lessonsLearned: [],
    whatWorkedWell: [],
    areasForImprovement: [],

    // Supporting Documents
    documentUrl: '',
    supportingDocuments: [],
    cparsDocumentUrl: '',
    contractDocumentUrl: '',

    // Verification
    verified: false,
    verificationDate: '',
    verificationSource: ''
  });

  const [newRecord, setNewRecord] = useState<PerformanceRecord>(getEmptyRecord());

  // Form section state - which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['identification', 'value']));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Helper to get current record (either editing or new)
  const getCurrentRecord = () => editingIndex !== null ? performances[editingIndex] : newRecord;

  // Helper to update a field
  const updateField = (field: keyof PerformanceRecord, value: any) => {
    if (editingIndex !== null) {
      const updated = [...performances];
      updated[editingIndex] = { ...updated[editingIndex], [field]: value };
      setPerformances(updated);
    } else {
      setNewRecord(prev => ({ ...prev, [field]: value }));
    }
  };

  // Helper to update nested client reference field
  const updateClientReference = (field: string, value: string) => {
    const current = getCurrentRecord();
    const updated = { ...current.clientReference, [field]: value };
    updateField('clientReference', updated);
  };

  // Generic array field handlers
  type ArrayField = 'technologiesUsed' | 'keyAccomplishments' | 'lessonsLearned' | 'projectObjectives' |
    'complianceStandards' | 'qualityStandards' | 'innovations' | 'serviceAreas' | 'demonstratedCapabilities' |
    'applicableNaics' | 'targetAgencies' | 'relevanceTags' | 'farClausesComplied' | 'whatWorkedWell' | 'areasForImprovement';

  const addToArrayField = (field: ArrayField, value: string) => {
    if (!value.trim()) return;
    const current = getCurrentRecord();
    const currentArray = (current[field] as string[]) || [];
    updateField(field, [...currentArray, value.trim()]);
  };

  const removeFromArrayField = (field: ArrayField, index: number) => {
    const current = getCurrentRecord();
    const currentArray = (current[field] as string[]) || [];
    updateField(field, currentArray.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (profile?.id) {
      fetchPerformances();
    }
  }, [profile?.id]);

  const fetchPerformances = async () => {
    if (!profile?.id) return;

    try {
      const { data, error } = await supabase
        .from('past_performance')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map database fields to component structure
      const mappedPerformances = (data || []).map(record => ({
        id: record.id,

        // Contract Identification
        projectName: record.project_name || '',
        contractNumber: record.contract_number || '',
        sectorType: record.sector_type || 'Government',
        agency: record.agency || '',
        subAgency: record.sub_agency || '',
        contractingOfficeAddress: record.contracting_office_address || {},
        naicsCode: record.naics_code || '',
        pscCode: record.psc_code || '',
        contractType: record.contract_type || 'Prime',
        rolePercentage: record.role_percentage ?? 100,

        // Contract Value & Period
        contractValue: record.contract_value || 0,
        ceilingValue: record.ceiling_value || 0,
        startDate: record.start_date || '',
        endDate: record.end_date || '',
        optionYears: record.option_years || 0,
        popType: record.pop_type || '',
        popLocation: record.pop_location || '',

        // Contract Vehicle
        contractVehicle: record.contract_vehicle || '',
        vehicleNumber: record.vehicle_number || '',
        taskOrderNumber: record.task_order_number || '',

        // Scope of Work
        scopeDescription: record.scope_description || '',
        projectObjectives: record.project_objectives || [],
        deliverables: record.deliverables || '',
        structuredDeliverables: record.structured_deliverables || [],
        serviceAreas: record.service_areas || [],

        // Technical Approach
        technicalApproach: record.technical_approach || '',
        technologiesUsed: record.technologies_used || [],
        complianceStandards: record.compliance_standards || [],
        securityRequirements: record.security_requirements || '',
        qualityStandards: record.quality_standards || [],

        // Key Accomplishments
        keyAccomplishments: record.key_accomplishments || [],
        innovations: record.innovations || [],

        // Performance Metrics
        performanceRating: textToNumericRating(record.performance_rating),
        clientSatisfaction: record.client_satisfaction || record.client_satisfaction_score || 10,
        onTimeDelivery: record.on_time_delivery ?? true,
        withinBudget: record.within_budget ?? true,

        // CPARS Ratings
        cparsRecordId: record.cpars_record_id || '',
        cparsQualityRating: record.cpars_quality_rating,
        cparsScheduleRating: record.cpars_schedule_rating,
        cparsCostRating: record.cpars_cost_rating,
        cparsManagementRating: record.cpars_management_rating,
        cparsSmallBusinessRating: record.cpars_small_business_rating,
        cparsOverallRating: record.cpars_overall_rating,
        cparsNarrative: record.cpars_narrative || '',

        // Team & Resources
        teamSize: record.team_size || 1,
        fteCount: record.fte_count || 0,
        projectComplexity: record.project_complexity || 5,
        revenueImpact: record.revenue_impact || 0,

        // Client Reference
        clientReference: record.client_reference || { name: '', title: '', email: '', phone: '', organization: '' },

        // Relevance & Reuse
        demonstratedCapabilities: record.demonstrated_capabilities || [],
        applicableNaics: record.applicable_naics || [],
        targetAgencies: record.target_agencies || [],
        relevanceTags: record.relevance_tags || [],

        // Compliance
        farClausesComplied: record.far_clauses_complied || [],
        subcontractingPlan: record.subcontracting_plan || {},
        limitationsCompliance: record.limitations_compliance || '',

        // Lessons Learned
        lessonsLearned: record.lessons_learned || [],
        whatWorkedWell: record.what_worked_well || [],
        areasForImprovement: record.areas_for_improvement || [],

        // Supporting Documents
        documentUrl: record.document_url || '',
        supportingDocuments: record.supporting_documents || [],
        cparsDocumentUrl: record.cpars_document_url || '',
        contractDocumentUrl: record.contract_document_url || '',

        // Verification
        verified: record.verified || false,
        verificationDate: record.verification_date || '',
        verificationSource: record.verification_source || ''
      }));

      setPerformances(mappedPerformances);
    } catch (error) {
      console.error('Error fetching performances:', error);
      toast.error('Failed to load performance data');
    }
  };

  // Enhanced: Calculate performance analytics
  const calculatePerformanceScore = () => {
    if (performances.length === 0) return 0;

    let score = 0;
    const maxScore = 100;

    // Number of projects (20 points)
    const projectScore = Math.min(performances.length / 5, 1) * 20;
    score += projectScore;

    // Average performance rating (25 points)
    const avgRating = performances.reduce((sum, p) => sum + p.performanceRating, 0) / performances.length;
    const ratingScore = (avgRating / 5) * 25;
    score += ratingScore;

    // Client satisfaction (20 points)
    const avgSatisfaction = performances.reduce((sum, p) => sum + p.clientSatisfaction, 0) / performances.length;
    const satisfactionScore = (avgSatisfaction / 10) * 20;
    score += satisfactionScore;

    // On-time delivery rate (15 points)
    const onTimeCount = performances.filter(p => p.onTimeDelivery).length;
    const onTimeScore = (onTimeCount / performances.length) * 15;
    score += onTimeScore;

    // Within budget rate (10 points)
    const budgetCount = performances.filter(p => p.withinBudget).length;
    const budgetScore = (budgetCount / performances.length) * 10;
    score += budgetScore;

    // Government vs private sector diversity (10 points)
    const govCount = performances.filter(p => p.sectorType === 'Government').length;
    const sectorDiversity = Math.min(govCount, performances.length - govCount) > 0 ? 10 : (govCount > 0 ? 5 : 0);
    score += sectorDiversity;

    return Math.round(Math.min(score, maxScore));
  };

  const performanceScore = calculatePerformanceScore();

  // Calculate additional metrics
  const totalContractValue = performances.reduce((sum, p) => sum + p.contractValue, 0);
  const avgContractValue = performances.length > 0 ? totalContractValue / performances.length : 0;
  const govProjectCount = performances.filter(p => p.sectorType === 'Government').length;
  const onTimeRate = performances.length > 0 ? (performances.filter(p => p.onTimeDelivery).length / performances.length) * 100 : 0;

  const addToArray = (field: 'technologiesUsed' | 'keyAccomplishments' | 'lessonsLearned', value: string) => {
    if (!value.trim()) return;

    if (editingIndex !== null) {
      const updated = [...performances];
      updated[editingIndex] = {
        ...updated[editingIndex],
        [field]: [...updated[editingIndex][field], value.trim()]
      };
      setPerformances(updated);
    } else {
      setNewRecord(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const removeFromArray = (field: 'technologiesUsed' | 'keyAccomplishments' | 'lessonsLearned', itemIndex: number) => {
    if (editingIndex !== null) {
      const updated = [...performances];
      updated[editingIndex] = {
        ...updated[editingIndex],
        [field]: updated[editingIndex][field].filter((_, i) => i !== itemIndex)
      };
      setPerformances(updated);
    } else {
      setNewRecord(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== itemIndex)
      }));
    }
  };

  const handleAddRecord = async () => {
    if (!profile?.id || !newRecord.projectName || !newRecord.agency) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('past_performance')
        .insert({
          profile_id: profile.id,

          // Contract Identification
          project_name: newRecord.projectName,
          contract_number: newRecord.contractNumber || null,
          sector_type: newRecord.sectorType,
          agency: newRecord.agency,
          sub_agency: newRecord.subAgency || null,
          contracting_office_address: newRecord.contractingOfficeAddress || null,
          naics_code: newRecord.naicsCode || null,
          psc_code: newRecord.pscCode || null,
          contract_type: newRecord.contractType,
          role_percentage: newRecord.rolePercentage,

          // Contract Value & Period
          contract_value: newRecord.contractValue,
          ceiling_value: newRecord.ceilingValue || null,
          start_date: newRecord.startDate || null,
          end_date: newRecord.endDate || null,
          option_years: newRecord.optionYears || null,
          pop_type: newRecord.popType || null,
          pop_location: newRecord.popLocation || null,

          // Contract Vehicle
          contract_vehicle: newRecord.contractVehicle || null,
          vehicle_number: newRecord.vehicleNumber || null,
          task_order_number: newRecord.taskOrderNumber || null,

          // Scope of Work
          scope_description: newRecord.scopeDescription || null,
          project_objectives: newRecord.projectObjectives.length > 0 ? newRecord.projectObjectives : null,
          deliverables: newRecord.deliverables || null,
          structured_deliverables: newRecord.structuredDeliverables.length > 0 ? newRecord.structuredDeliverables : null,
          service_areas: newRecord.serviceAreas.length > 0 ? newRecord.serviceAreas : null,

          // Technical Approach
          technical_approach: newRecord.technicalApproach || null,
          technologies_used: newRecord.technologiesUsed.length > 0 ? newRecord.technologiesUsed : null,
          compliance_standards: newRecord.complianceStandards.length > 0 ? newRecord.complianceStandards : null,
          security_requirements: newRecord.securityRequirements || null,
          quality_standards: newRecord.qualityStandards.length > 0 ? newRecord.qualityStandards : null,

          // Key Accomplishments
          key_accomplishments: newRecord.keyAccomplishments.length > 0 ? newRecord.keyAccomplishments : null,
          innovations: newRecord.innovations.length > 0 ? newRecord.innovations : null,

          // Performance Metrics
          performance_rating: numericToTextRating(newRecord.performanceRating),
          client_satisfaction: newRecord.clientSatisfaction,
          on_time_delivery: newRecord.onTimeDelivery,
          within_budget: newRecord.withinBudget,

          // CPARS Ratings
          cpars_record_id: newRecord.cparsRecordId || null,
          cpars_quality_rating: newRecord.cparsQualityRating,
          cpars_schedule_rating: newRecord.cparsScheduleRating,
          cpars_cost_rating: newRecord.cparsCostRating,
          cpars_management_rating: newRecord.cparsManagementRating,
          cpars_small_business_rating: newRecord.cparsSmallBusinessRating,
          cpars_overall_rating: newRecord.cparsOverallRating,
          cpars_narrative: newRecord.cparsNarrative || null,

          // Team & Resources
          team_size: newRecord.teamSize,
          fte_count: newRecord.fteCount || null,
          project_complexity: newRecord.projectComplexity,
          revenue_impact: newRecord.revenueImpact || null,

          // Client Reference
          client_reference: newRecord.clientReference,

          // Relevance & Reuse
          demonstrated_capabilities: newRecord.demonstratedCapabilities.length > 0 ? newRecord.demonstratedCapabilities : null,
          applicable_naics: newRecord.applicableNaics.length > 0 ? newRecord.applicableNaics : null,
          target_agencies: newRecord.targetAgencies.length > 0 ? newRecord.targetAgencies : null,
          relevance_tags: newRecord.relevanceTags.length > 0 ? newRecord.relevanceTags : null,

          // Compliance
          far_clauses_complied: newRecord.farClausesComplied.length > 0 ? newRecord.farClausesComplied : null,
          subcontracting_plan: Object.keys(newRecord.subcontractingPlan).length > 0 ? newRecord.subcontractingPlan : null,
          limitations_compliance: newRecord.limitationsCompliance || null,

          // Lessons Learned
          lessons_learned: newRecord.lessonsLearned.length > 0 ? newRecord.lessonsLearned : null,
          what_worked_well: newRecord.whatWorkedWell.length > 0 ? newRecord.whatWorkedWell : null,
          areas_for_improvement: newRecord.areasForImprovement.length > 0 ? newRecord.areasForImprovement : null,

          // Supporting Documents
          document_url: newRecord.documentUrl || null,
          supporting_documents: newRecord.supportingDocuments.length > 0 ? newRecord.supportingDocuments : null,
          cpars_document_url: newRecord.cparsDocumentUrl || null,
          contract_document_url: newRecord.contractDocumentUrl || null,

          // Verification
          verified: newRecord.verified,
          verification_date: newRecord.verificationDate || null,
          verification_source: newRecord.verificationSource || null
        });

      if (error) throw error;

      toast.success('Performance record added successfully');
      setNewRecord(getEmptyRecord());
      setShowAddForm(false);
      fetchPerformances();
    } catch (error) {
      console.error('Error adding performance record:', error);
      toast.error('Failed to add performance record');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecord = async (index: number) => {
    if (!profile?.id) return;

    const record = performances[index];
    if (!record.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('past_performance')
        .update({
          // Contract Identification
          project_name: record.projectName,
          contract_number: record.contractNumber || null,
          sector_type: record.sectorType,
          agency: record.agency,
          sub_agency: record.subAgency || null,
          contracting_office_address: record.contractingOfficeAddress || null,
          naics_code: record.naicsCode || null,
          psc_code: record.pscCode || null,
          contract_type: record.contractType,
          role_percentage: record.rolePercentage,

          // Contract Value & Period
          contract_value: record.contractValue,
          ceiling_value: record.ceilingValue || null,
          start_date: record.startDate || null,
          end_date: record.endDate || null,
          option_years: record.optionYears || null,
          pop_type: record.popType || null,
          pop_location: record.popLocation || null,

          // Contract Vehicle
          contract_vehicle: record.contractVehicle || null,
          vehicle_number: record.vehicleNumber || null,
          task_order_number: record.taskOrderNumber || null,

          // Scope of Work
          scope_description: record.scopeDescription || null,
          project_objectives: record.projectObjectives.length > 0 ? record.projectObjectives : null,
          deliverables: record.deliverables || null,
          structured_deliverables: record.structuredDeliverables.length > 0 ? record.structuredDeliverables : null,
          service_areas: record.serviceAreas.length > 0 ? record.serviceAreas : null,

          // Technical Approach
          technical_approach: record.technicalApproach || null,
          technologies_used: record.technologiesUsed.length > 0 ? record.technologiesUsed : null,
          compliance_standards: record.complianceStandards.length > 0 ? record.complianceStandards : null,
          security_requirements: record.securityRequirements || null,
          quality_standards: record.qualityStandards.length > 0 ? record.qualityStandards : null,

          // Key Accomplishments
          key_accomplishments: record.keyAccomplishments.length > 0 ? record.keyAccomplishments : null,
          innovations: record.innovations.length > 0 ? record.innovations : null,

          // Performance Metrics
          performance_rating: numericToTextRating(record.performanceRating),
          client_satisfaction: record.clientSatisfaction,
          on_time_delivery: record.onTimeDelivery,
          within_budget: record.withinBudget,

          // CPARS Ratings
          cpars_record_id: record.cparsRecordId || null,
          cpars_quality_rating: record.cparsQualityRating,
          cpars_schedule_rating: record.cparsScheduleRating,
          cpars_cost_rating: record.cparsCostRating,
          cpars_management_rating: record.cparsManagementRating,
          cpars_small_business_rating: record.cparsSmallBusinessRating,
          cpars_overall_rating: record.cparsOverallRating,
          cpars_narrative: record.cparsNarrative || null,

          // Team & Resources
          team_size: record.teamSize,
          fte_count: record.fteCount || null,
          project_complexity: record.projectComplexity,
          revenue_impact: record.revenueImpact || null,

          // Client Reference
          client_reference: record.clientReference,

          // Relevance & Reuse
          demonstrated_capabilities: record.demonstratedCapabilities.length > 0 ? record.demonstratedCapabilities : null,
          applicable_naics: record.applicableNaics.length > 0 ? record.applicableNaics : null,
          target_agencies: record.targetAgencies.length > 0 ? record.targetAgencies : null,
          relevance_tags: record.relevanceTags.length > 0 ? record.relevanceTags : null,

          // Compliance
          far_clauses_complied: record.farClausesComplied.length > 0 ? record.farClausesComplied : null,
          subcontracting_plan: Object.keys(record.subcontractingPlan || {}).length > 0 ? record.subcontractingPlan : null,
          limitations_compliance: record.limitationsCompliance || null,

          // Lessons Learned
          lessons_learned: record.lessonsLearned.length > 0 ? record.lessonsLearned : null,
          what_worked_well: record.whatWorkedWell.length > 0 ? record.whatWorkedWell : null,
          areas_for_improvement: record.areasForImprovement.length > 0 ? record.areasForImprovement : null,

          // Supporting Documents
          document_url: record.documentUrl || null,
          supporting_documents: record.supportingDocuments.length > 0 ? record.supportingDocuments : null,
          cpars_document_url: record.cparsDocumentUrl || null,
          contract_document_url: record.contractDocumentUrl || null,

          // Verification
          verified: record.verified,
          verification_date: record.verificationDate || null,
          verification_source: record.verificationSource || null,

          updated_at: new Date().toISOString()
        })
        .eq('id', record.id);

      if (error) throw error;

      toast.success('Performance record updated successfully');
      setEditingIndex(null);
    } catch (error) {
      console.error('Error updating performance record:', error);
      toast.error('Failed to update performance record');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (index: number) => {
    const record = performances[index];
    if (!record.id) return;

    if (!confirm('Are you sure you want to delete this performance record?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('past_performance')
        .delete()
        .eq('id', record.id);

      if (error) throw error;

      toast.success('Performance record deleted successfully');
      fetchPerformances();
    } catch (error) {
      console.error('Error deleting performance record:', error);
      toast.error('Failed to delete performance record');
    } finally {
      setLoading(false);
    }
  };

  // Document Import Handlers
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setImporting(true);
    setShowImportModal(true);
    setExtractedProjects([]);
    setSelectedImports(new Set());

    try {
      toast.loading('Extracting past performance data...', { id: 'pp-extraction' });

      const result = await DocumentAnalyzer.processAndExtract(file, {
        documentType: 'Past Performance',
        extractionMode: 'past_performance'
      });

      toast.dismiss('pp-extraction');

      if (result.success && result.pastPerformance.length > 0) {
        setExtractedProjects(result.pastPerformance);
        setSelectedImports(new Set(result.pastPerformance.map((_, i) => i)));
        toast.success(`Found ${result.pastPerformance.length} past performance record(s)`);
      } else {
        toast.error('No past performance records found in document');
        if (result.extractionNotes.length > 0) {
          console.log('Extraction notes:', result.extractionNotes);
        }
      }
    } catch (error: any) {
      toast.dismiss('pp-extraction');
      console.error('Extraction error:', error);
      toast.error(`Failed to extract: ${error.message}`);
    } finally {
      setImporting(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const toggleImportSelection = (index: number) => {
    setSelectedImports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleImportSelected = async () => {
    if (!profile?.id || selectedImports.size === 0) return;

    setLoading(true);
    try {
      const projectsToImport = extractedProjects.filter((_, i) => selectedImports.has(i));

      const records = projectsToImport.map(pp => ({
        profile_id: profile.id,

        // Contract Identification
        project_name: pp.projectName,
        contract_number: pp.contractNumber || null,
        agency: pp.agency,
        sub_agency: pp.subAgency || null,
        sector_type: pp.sectorType,
        contract_type: pp.contractType,
        // Don't claim 100% self-performed for a sub/JV when the extractor didn't find a
        // share — default non-primes to a partial 50% so the record isn't overstated.
        role_percentage: pp.rolePercentage ?? (pp.contractType === 'Prime' ? 100 : 50),
        naics_code: pp.naicsCode || null,
        psc_code: pp.pscCode || null,

        // Contract Value & Period
        contract_value: pp.contractValue || 0,
        ceiling_value: pp.ceilingValue || null,
        start_date: pp.startDate || null,
        end_date: pp.endDate || null,
        option_years: pp.optionYears || null,
        pop_type: pp.popType || null,
        pop_location: pp.popLocation || null,

        // Contract Vehicle
        contract_vehicle: pp.contractVehicle || null,
        vehicle_number: pp.vehicleNumber || null,
        task_order_number: pp.taskOrderNumber || null,

        // Scope of Work
        scope_description: pp.scopeDescription || null,
        project_objectives: pp.projectObjectives?.length > 0 ? pp.projectObjectives : null,
        deliverables: pp.deliverables || null,
        service_areas: pp.serviceAreas?.length > 0 ? pp.serviceAreas : null,

        // Technical Approach
        technical_approach: pp.technicalApproach || null,
        technologies_used: pp.technologiesUsed?.length > 0 ? pp.technologiesUsed : null,
        compliance_standards: pp.complianceStandards?.length > 0 ? pp.complianceStandards : null,
        security_requirements: pp.securityRequirements || null,
        quality_standards: pp.qualityStandards?.length > 0 ? pp.qualityStandards : null,

        // Accomplishments
        key_accomplishments: pp.keyAccomplishments?.length > 0 ? pp.keyAccomplishments : null,
        innovations: pp.innovations?.length > 0 ? pp.innovations : null,

        // Performance Metrics - performance_rating is TEXT enum in DB
        // Must be: 'exceptional', 'satisfactory', 'marginal', 'unsatisfactory'
        performance_rating: numericToTextRating(pp.performanceRating || 5),
        client_satisfaction: (pp.clientSatisfaction >= 1 && pp.clientSatisfaction <= 10) ? pp.clientSatisfaction : null,
        on_time_delivery: pp.onTimeDelivery ?? true,
        within_budget: pp.withinBudget ?? true,

        // CPARS Ratings - must be 1-5 or null
        cpars_record_id: pp.cparsRecordId || null,
        cpars_quality_rating: (pp.cparsQualityRating >= 1 && pp.cparsQualityRating <= 5) ? pp.cparsQualityRating : null,
        cpars_schedule_rating: (pp.cparsScheduleRating >= 1 && pp.cparsScheduleRating <= 5) ? pp.cparsScheduleRating : null,
        cpars_cost_rating: (pp.cparsCostRating >= 1 && pp.cparsCostRating <= 5) ? pp.cparsCostRating : null,
        cpars_management_rating: (pp.cparsManagementRating >= 1 && pp.cparsManagementRating <= 5) ? pp.cparsManagementRating : null,
        cpars_small_business_rating: (pp.cparsSmallBusinessRating >= 1 && pp.cparsSmallBusinessRating <= 5) ? pp.cparsSmallBusinessRating : null,
        cpars_overall_rating: (pp.cparsOverallRating >= 1 && pp.cparsOverallRating <= 5) ? pp.cparsOverallRating : null,

        // Team
        team_size: pp.teamSize || null,
        fte_count: pp.fteCount || null,

        // Client Reference
        client_reference: pp.clientReference || null,

        // Relevance
        demonstrated_capabilities: pp.demonstratedCapabilities?.length > 0 ? pp.demonstratedCapabilities : null,
        target_agencies: pp.targetAgencies?.length > 0 ? pp.targetAgencies : null,

        // Lessons Learned
        what_worked_well: pp.whatWorkedWell?.length > 0 ? pp.whatWorkedWell : null,
        areas_for_improvement: pp.areasForImprovement?.length > 0 ? pp.areasForImprovement : null,
        lessons_learned: pp.lessonsLearned?.length > 0 ? pp.lessonsLearned : null
      }));

      const { error } = await supabase
        .from('past_performance')
        .insert(records);

      if (error) throw error;

      toast.success(`Imported ${records.length} past performance record(s)`);
      setShowImportModal(false);
      setExtractedProjects([]);
      setSelectedImports(new Set());
      fetchPerformances();
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(`Failed to import: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ rating, onChange, readOnly = false }: { rating: number; onChange?: (rating: number) => void; readOnly?: boolean }) => (
    <div className="flex space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readOnly && onChange && onChange(star)}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer'} ${
            star <= rating ? 'text-yellow-400' : 'text-gray-300'
          }`}
          disabled={readOnly}
        >
          <Star className="h-4 w-4 fill-current" />
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Enhanced: Performance Analytics Dashboard */}
      <div className="bg-bw-surface-subtle rounded-lg p-6 border border-green-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <Award className="h-5 w-5 mr-2 text-success" />
            Performance Analytics
          </h3>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            performanceScore >= 80 ? 'text-success bg-success-bg' :
            performanceScore >= 60 ? 'text-fit-moderate bg-fit-moderate-bg' : 'text-urgent bg-urgent-bg'
          }`}>
            {performanceScore}% Score
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Performance Score</span>
              <span className="text-lg font-bold text-bw-ink">{performanceScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  performanceScore >= 80 ? 'bg-success-bg0' :
                  performanceScore >= 60 ? 'bg-fit-moderate-bg0' : 'bg-urgent-bg0'
                }`}
                style={{ width: `${performanceScore}%` }}
              />
            </div>
          </div>

          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Total Projects</span>
              <span className="text-lg font-bold text-success">{performances.length}</span>
            </div>
            <div className="text-xs text-bw-muted mt-1">{govProjectCount} Government</div>
          </div>

          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Contract Value</span>
              <span className="text-lg font-bold text-success">
                ${totalContractValue > 1000000 ? `${(totalContractValue / 1000000).toFixed(1)}M` : `${(totalContractValue / 1000).toFixed(0)}K`}
              </span>
            </div>
            <div className="text-xs text-bw-muted mt-1">Total portfolio</div>
          </div>

          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">On-Time Rate</span>
              <span className="text-lg font-bold text-bw-primary">{onTimeRate.toFixed(0)}%</span>
            </div>
            <div className="text-xs text-bw-muted mt-1">Delivery performance</div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-bw-surface rounded-lg border">
          <div className="flex items-center text-sm text-bw-muted">
            <TrendingUp className="h-4 w-4 mr-2" />
            <span>Performance Trend: </span>
            {performanceScore < 60 && (
              <span className="text-urgent ml-1">Add more successful projects and improve delivery metrics</span>
            )}
            {performanceScore >= 60 && performanceScore < 80 && (
              <span className="text-fit-moderate ml-1">Strong foundation - focus on client satisfaction and references</span>
            )}
            {performanceScore >= 80 && (
              <span className="text-success ml-1">Excellent track record! Leverage this for competitive advantage</span>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input for document import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx"
        className="hidden"
      />

      {/* Add Record Buttons */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-bw-ink">Past Performance</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {importing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="h-4 w-4 mr-2" />
            )}
            Import from Document
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center px-4 py-2 bg-success text-white rounded-md hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Project
          </button>
        </div>
      </div>

      {/* Add/Edit Record Form - Comprehensive with Collapsible Sections */}
      {(showAddForm || editingIndex !== null) && (
        <div className="bg-bw-surface rounded-lg border shadow-sm">
          {/* Form Header */}
          <div className="flex justify-between items-center p-6 border-b bg-bw-surface-subtle">
            <div>
              <h3 className="text-lg font-semibold text-bw-ink">
                {editingIndex !== null ? 'Edit Performance Record' : 'Add New Performance Record'}
              </h3>
              <p className="text-sm text-bw-muted mt-1">
                Complete as many fields as possible for better proposal matching
              </p>
            </div>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingIndex(null);
                setExpandedSections(new Set(['identification', 'value']));
              }}
              className="text-bw-muted hover:text-bw-muted"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="divide-y">
            {/* SECTION 1: Contract Identification */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('identification')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Contract Identification</span>
                  <span className="ml-2 text-xs text-urgent">* Required</span>
                </div>
                {expandedSections.has('identification') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('identification') && (
                <div className="px-6 pb-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Project Name *</label>
                      <input
                        type="text"
                        value={getCurrentRecord().projectName}
                        onChange={(e) => updateField('projectName', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., GitHub Course Development & Delivery"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Contract Number</label>
                      <input
                        type="text"
                        value={getCurrentRecord().contractNumber}
                        onChange={(e) => updateField('contractNumber', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., 36C10B23Q0517"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Agency/Client *</label>
                      <input
                        type="text"
                        value={getCurrentRecord().agency}
                        onChange={(e) => updateField('agency', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., Department of Veterans Affairs"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Sub-Agency/Office</label>
                      <input
                        type="text"
                        value={getCurrentRecord().subAgency}
                        onChange={(e) => updateField('subAgency', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., Technology Acquisition Center (TAC)"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Sector Type</label>
                      <select
                        value={getCurrentRecord().sectorType}
                        onChange={(e) => updateField('sectorType', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      >
                        <option value="Government">Government</option>
                        <option value="Private Sector">Private Sector</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Contract Type</label>
                      <select
                        value={getCurrentRecord().contractType}
                        onChange={(e) => updateField('contractType', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      >
                        <option value="Prime">Prime Contractor</option>
                        <option value="Subcontractor">Subcontractor</option>
                        <option value="JV">Joint Venture</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Self-Performed %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={getCurrentRecord().rolePercentage}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          updateField('rolePercentage', Number.isNaN(n) ? 0 : Math.min(100, Math.max(0, n)));
                        }}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">NAICS Code</label>
                      <input
                        type="text"
                        value={getCurrentRecord().naicsCode}
                        onChange={(e) => updateField('naicsCode', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., 611420"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">PSC Code</label>
                      <input
                        type="text"
                        value={getCurrentRecord().pscCode}
                        onChange={(e) => updateField('pscCode', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., U008"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 2: Contract Value & Period */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('value')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Contract Value & Period</span>
                </div>
                {expandedSections.has('value') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('value') && (
                <div className="px-6 pb-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Contract Value ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={getCurrentRecord().contractValue || ''}
                        onChange={(e) => updateField('contractValue', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., 9570"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Ceiling Value ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={getCurrentRecord().ceilingValue || ''}
                        onChange={(e) => updateField('ceilingValue', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="For IDIQ/task orders"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Option Years</label>
                      <input
                        type="number"
                        min="0"
                        value={getCurrentRecord().optionYears || ''}
                        onChange={(e) => updateField('optionYears', parseInt(e.target.value) || 0)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Start Date</label>
                      <input
                        type="date"
                        value={getCurrentRecord().startDate}
                        onChange={(e) => updateField('startDate', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">End Date</label>
                      <input
                        type="date"
                        value={getCurrentRecord().endDate}
                        onChange={(e) => updateField('endDate', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Contract Vehicle</label>
                      <select
                        value={getCurrentRecord().contractVehicle}
                        onChange={(e) => updateField('contractVehicle', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      >
                        <option value="">Select vehicle type...</option>
                        <option value="GSA Schedule">GSA Schedule</option>
                        <option value="GWAC">GWAC</option>
                        <option value="BPA">BPA</option>
                        <option value="IDIQ">IDIQ</option>
                        <option value="Standalone">Standalone</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Vehicle Number</label>
                      <input
                        type="text"
                        value={getCurrentRecord().vehicleNumber}
                        onChange={(e) => updateField('vehicleNumber', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., GS-35F-0511T"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Task Order Number</label>
                      <input
                        type="text"
                        value={getCurrentRecord().taskOrderNumber}
                        onChange={(e) => updateField('taskOrderNumber', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Place of Performance Type</label>
                      <select
                        value={getCurrentRecord().popType}
                        onChange={(e) => updateField('popType', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      >
                        <option value="">Select...</option>
                        <option value="Virtual">Virtual</option>
                        <option value="On-site">On-site</option>
                        <option value="Hybrid">Hybrid</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Place of Performance Location</label>
                      <input
                        type="text"
                        value={getCurrentRecord().popLocation}
                        onChange={(e) => updateField('popLocation', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="e.g., Washington, DC or FedRAMP-authorized platform"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 3: Scope of Work */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('scope')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Scope of Work & Deliverables</span>
                </div>
                {expandedSections.has('scope') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('scope') && (
                <div className="px-6 pb-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-1">Scope Description</label>
                    <textarea
                      value={getCurrentRecord().scopeDescription}
                      onChange={(e) => updateField('scopeDescription', e.target.value)}
                      rows={4}
                      className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      placeholder="Detailed description of the scope of work..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-1">Deliverables Summary</label>
                    <textarea
                      value={getCurrentRecord().deliverables}
                      onChange={(e) => updateField('deliverables', e.target.value)}
                      rows={3}
                      className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      placeholder="Summarize key deliverables..."
                    />
                  </div>
                  {/* Project Objectives Array */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Project Objectives</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().projectObjectives || []).map((obj, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-primary-tint text-bw-primary">
                          {obj}
                          <button type="button" onClick={() => removeFromArrayField('projectObjectives', index)} className="ml-2 text-bw-primary hover:text-bw-primary">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add objective..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('projectObjectives', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('projectObjectives', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Service Areas Array */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Service Areas</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().serviceAreas || []).map((area, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-primary-tint text-bw-primary">
                          {area}
                          <button type="button" onClick={() => removeFromArrayField('serviceAreas', index)} className="ml-2 text-bw-primary hover:text-bw-primary">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add service area..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('serviceAreas', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('serviceAreas', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 4: Technical Approach */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('technical')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Technical Approach & Compliance</span>
                </div>
                {expandedSections.has('technical') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('technical') && (
                <div className="px-6 pb-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-1">Technical Approach/Methodology</label>
                    <textarea
                      value={getCurrentRecord().technicalApproach}
                      onChange={(e) => updateField('technicalApproach', e.target.value)}
                      rows={3}
                      className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      placeholder="Describe the technical approach and methodology used..."
                    />
                  </div>
                  {/* Technologies Used */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Technologies Used</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().technologiesUsed || []).map((tech, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-success-bg text-success">
                          {tech}
                          <button type="button" onClick={() => removeFromArrayField('technologiesUsed', index)} className="ml-2 text-success hover:text-success">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add technology..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('technologiesUsed', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('technologiesUsed', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Compliance Standards */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Compliance Standards Met</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().complianceStandards || []).map((std, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-primary-tint text-bw-primary">
                          {std}
                          <button type="button" onClick={() => removeFromArrayField('complianceStandards', index)} className="ml-2 text-bw-primary hover:text-bw-primary">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="e.g., Section 508, FedRAMP, FISMA..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('complianceStandards', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('complianceStandards', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Quality Standards */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Quality Standards Applied</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().qualityStandards || []).map((std, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-fit-moderate-bg text-fit-moderate">
                          {std}
                          <button type="button" onClick={() => removeFromArrayField('qualityStandards', index)} className="ml-2 text-fit-moderate hover:text-fit-moderate">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="e.g., ISO 9001, CMMI Level 3..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('qualityStandards', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('qualityStandards', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-1">Security Requirements</label>
                    <input
                      type="text"
                      value={getCurrentRecord().securityRequirements}
                      onChange={(e) => updateField('securityRequirements', e.target.value)}
                      className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      placeholder="e.g., Secret clearance required, NIST 800-53 compliance"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 5: Performance & CPARS */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('performance')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Performance Ratings & CPARS</span>
                </div>
                {expandedSections.has('performance') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('performance') && (
                <div className="px-6 pb-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Performance Rating (Internal)</label>
                      <StarRating
                        rating={getCurrentRecord().performanceRating}
                        onChange={(rating) => updateField('performanceRating', rating)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Client Satisfaction (1-10)</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={getCurrentRecord().clientSatisfaction}
                        onChange={(e) => updateField('clientSatisfaction', parseInt(e.target.value) || 10)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input type="checkbox" checked={getCurrentRecord().onTimeDelivery} onChange={(e) => updateField('onTimeDelivery', e.target.checked)}
                        className="mr-2 rounded border-bw-border text-success focus:ring-green-500" />
                      <span className="text-sm font-medium text-bw-ink">On-Time Delivery</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" checked={getCurrentRecord().withinBudget} onChange={(e) => updateField('withinBudget', e.target.checked)}
                        className="mr-2 rounded border-bw-border text-success focus:ring-green-500" />
                      <span className="text-sm font-medium text-bw-ink">Within Budget</span>
                    </label>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-bw-ink mb-3">CPARS Ratings (if available)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-bw-muted mb-1">Quality</label>
                        <select value={getCurrentRecord().cparsQualityRating ?? ''} onChange={(e) => updateField('cparsQualityRating', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                          <option value="">N/A</option>
                          <option value="5">5 - Exceptional</option>
                          <option value="4">4 - Very Good</option>
                          <option value="3">3 - Satisfactory</option>
                          <option value="2">2 - Marginal</option>
                          <option value="1">1 - Unsatisfactory</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-muted mb-1">Schedule</label>
                        <select value={getCurrentRecord().cparsScheduleRating ?? ''} onChange={(e) => updateField('cparsScheduleRating', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                          <option value="">N/A</option>
                          <option value="5">5 - Exceptional</option>
                          <option value="4">4 - Very Good</option>
                          <option value="3">3 - Satisfactory</option>
                          <option value="2">2 - Marginal</option>
                          <option value="1">1 - Unsatisfactory</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-muted mb-1">Cost Control</label>
                        <select value={getCurrentRecord().cparsCostRating ?? ''} onChange={(e) => updateField('cparsCostRating', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                          <option value="">N/A</option>
                          <option value="5">5 - Exceptional</option>
                          <option value="4">4 - Very Good</option>
                          <option value="3">3 - Satisfactory</option>
                          <option value="2">2 - Marginal</option>
                          <option value="1">1 - Unsatisfactory</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-muted mb-1">Management</label>
                        <select value={getCurrentRecord().cparsManagementRating ?? ''} onChange={(e) => updateField('cparsManagementRating', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                          <option value="">N/A</option>
                          <option value="5">5 - Exceptional</option>
                          <option value="4">4 - Very Good</option>
                          <option value="3">3 - Satisfactory</option>
                          <option value="2">2 - Marginal</option>
                          <option value="1">1 - Unsatisfactory</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-muted mb-1">Small Business</label>
                        <select value={getCurrentRecord().cparsSmallBusinessRating ?? ''} onChange={(e) => updateField('cparsSmallBusinessRating', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                          <option value="">N/A</option>
                          <option value="5">5 - Exceptional</option>
                          <option value="4">4 - Very Good</option>
                          <option value="3">3 - Satisfactory</option>
                          <option value="2">2 - Marginal</option>
                          <option value="1">1 - Unsatisfactory</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-muted mb-1">Overall</label>
                        <select value={getCurrentRecord().cparsOverallRating ?? ''} onChange={(e) => updateField('cparsOverallRating', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                          <option value="">N/A</option>
                          <option value="5">5 - Exceptional</option>
                          <option value="4">4 - Very Good</option>
                          <option value="3">3 - Satisfactory</option>
                          <option value="2">2 - Marginal</option>
                          <option value="1">1 - Unsatisfactory</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-bw-ink mb-1">CPARS Record ID</label>
                      <input type="text" value={getCurrentRecord().cparsRecordId} onChange={(e) => updateField('cparsRecordId', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" placeholder="CPARS record identifier" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 6: Accomplishments & Outcomes */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('accomplishments')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Accomplishments & Outcomes</span>
                </div>
                {expandedSections.has('accomplishments') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('accomplishments') && (
                <div className="px-6 pb-6 space-y-4">
                  {/* Key Accomplishments */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Key Accomplishments</label>
                    <div className="space-y-2 mb-2">
                      {(getCurrentRecord().keyAccomplishments || []).map((acc, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-success-bg rounded-md">
                          <span className="flex-1 text-sm text-bw-ink">{acc}</span>
                          <button type="button" onClick={() => removeFromArrayField('keyAccomplishments', index)} className="text-success hover:text-success">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add accomplishment..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('keyAccomplishments', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('keyAccomplishments', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Innovations */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Innovations</label>
                    <div className="space-y-2 mb-2">
                      {(getCurrentRecord().innovations || []).map((inn, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-bw-primary-tint rounded-md">
                          <span className="flex-1 text-sm text-bw-ink">{inn}</span>
                          <button type="button" onClick={() => removeFromArrayField('innovations', index)} className="text-bw-primary hover:text-bw-primary">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add innovation..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('innovations', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('innovations', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Team Size</label>
                      <input type="number" min="1" value={getCurrentRecord().teamSize || ''} onChange={(e) => updateField('teamSize', parseInt(e.target.value) || 1)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">FTE Count</label>
                      <input type="number" min="0" step="0.5" value={getCurrentRecord().fteCount || ''} onChange={(e) => updateField('fteCount', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Project Complexity (1-10)</label>
                      <input type="number" min="1" max="10" value={getCurrentRecord().projectComplexity || ''} onChange={(e) => updateField('projectComplexity', parseInt(e.target.value) || 5)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 7: Client Reference */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('reference')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Client Reference</span>
                </div>
                {expandedSections.has('reference') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('reference') && (
                <div className="px-6 pb-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Name</label>
                      <input type="text" value={getCurrentRecord().clientReference?.name || ''} onChange={(e) => updateClientReference('name', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" placeholder="e.g., Kimberly Geran" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Title</label>
                      <input type="text" value={getCurrentRecord().clientReference?.title || ''} onChange={(e) => updateClientReference('title', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" placeholder="e.g., Procurement Analyst" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Email</label>
                      <input type="email" value={getCurrentRecord().clientReference?.email || ''} onChange={(e) => updateClientReference('email', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" placeholder="e.g., name@agency.gov" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1">Phone</label>
                      <input type="tel" value={getCurrentRecord().clientReference?.phone || ''} onChange={(e) => updateClientReference('phone', e.target.value)}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" placeholder="e.g., (555) 123-4567" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-1">Organization</label>
                    <input type="text" value={getCurrentRecord().clientReference?.organization || ''} onChange={(e) => updateClientReference('organization', e.target.value)}
                      className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500" placeholder="e.g., VA Technology Acquisition Center" />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 8: Lessons Learned */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('lessons')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <Lightbulb className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Lessons Learned</span>
                </div>
                {expandedSections.has('lessons') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('lessons') && (
                <div className="px-6 pb-6 space-y-4">
                  {/* What Worked Well */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">What Worked Well</label>
                    <div className="space-y-2 mb-2">
                      {(getCurrentRecord().whatWorkedWell || []).map((item, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-success-bg rounded-md">
                          <span className="flex-1 text-sm text-bw-ink">{item}</span>
                          <button type="button" onClick={() => removeFromArrayField('whatWorkedWell', index)} className="text-success hover:text-success">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add what worked well..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('whatWorkedWell', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('whatWorkedWell', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Areas for Improvement */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Areas for Improvement</label>
                    <div className="space-y-2 mb-2">
                      {(getCurrentRecord().areasForImprovement || []).map((item, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-fit-moderate-bg rounded-md">
                          <span className="flex-1 text-sm text-bw-ink">{item}</span>
                          <button type="button" onClick={() => removeFromArrayField('areasForImprovement', index)} className="text-fit-moderate hover:text-fit-moderate">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add improvement area..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('areasForImprovement', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('areasForImprovement', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* General Lessons Learned */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Key Lessons Learned</label>
                    <div className="space-y-2 mb-2">
                      {(getCurrentRecord().lessonsLearned || []).map((item, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-bw-primary-tint rounded-md">
                          <span className="flex-1 text-sm text-bw-ink">{item}</span>
                          <button type="button" onClick={() => removeFromArrayField('lessonsLearned', index)} className="text-bw-primary hover:text-bw-primary">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add lesson learned..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('lessonsLearned', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('lessonsLearned', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 9: Relevance & Future Use */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('relevance')}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-bw-surface-subtle"
              >
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-success mr-3" />
                  <span className="font-medium text-bw-ink">Relevance & Future Opportunities</span>
                </div>
                {expandedSections.has('relevance') ? <ChevronUp className="h-5 w-5 text-bw-muted" /> : <ChevronDown className="h-5 w-5 text-bw-muted" />}
              </button>
              {expandedSections.has('relevance') && (
                <div className="px-6 pb-6 space-y-4">
                  {/* Demonstrated Capabilities */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Demonstrated Capabilities</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().demonstratedCapabilities || []).map((cap, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-success-bg text-success">
                          {cap}
                          <button type="button" onClick={() => removeFromArrayField('demonstratedCapabilities', index)} className="ml-2 text-success hover:text-success">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="e.g., Virtual Training Delivery, FedRAMP compliance..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('demonstratedCapabilities', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('demonstratedCapabilities', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Target Agencies */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Target Agencies (for future opportunities)</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().targetAgencies || []).map((agency, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-primary-tint text-bw-primary">
                          {agency}
                          <button type="button" onClick={() => removeFromArrayField('targetAgencies', index)} className="ml-2 text-bw-primary hover:text-bw-primary">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="e.g., DoD, GSA, DHS..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('targetAgencies', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('targetAgencies', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Relevance Tags */}
                  <div>
                    <label className="block text-sm font-medium text-bw-ink mb-2">Relevance Tags</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(getCurrentRecord().relevanceTags || []).map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-surface-subtle text-bw-ink">
                          {tag}
                          <button type="button" onClick={() => removeFromArrayField('relevanceTags', index)} className="ml-2 text-bw-muted hover:text-bw-ink">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Add tag for matching opportunities..." className="flex-1 rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToArrayField('relevanceTags', e.currentTarget.value); e.currentTarget.value = ''; }}} />
                      <button type="button" onClick={(e) => { const input = e.currentTarget.previousElementSibling as HTMLInputElement; addToArrayField('relevanceTags', input.value); input.value = ''; }}
                        className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-between items-center p-6 border-t bg-bw-surface-subtle">
            <button
              type="button"
              onClick={() => setExpandedSections(new Set(['identification', 'value', 'scope', 'technical', 'performance', 'accomplishments', 'reference', 'lessons', 'relevance']))}
              className="text-sm text-success hover:text-success"
            >
              Expand All Sections
            </button>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingIndex(null);
                  setExpandedSections(new Set(['identification', 'value']));
                }}
                className="px-4 py-2 border border-bw-border rounded-md text-bw-ink hover:bg-bw-surface-subtle"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingIndex !== null) {
                    handleUpdateRecord(editingIndex);
                  } else {
                    handleAddRecord();
                  }
                }}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-success text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : editingIndex !== null ? 'Update Record' : 'Add Record'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Performance Records List */}
      <div className="space-y-4">
        {performances.map((record, index) => (
          <div key={record.id || index} className="bg-bw-surface rounded-lg border p-6 shadow-sm">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-3">
                  <h3 className="text-lg font-semibold text-bw-ink">{record.projectName}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                    record.sectorType === 'Government' ? 'bg-bw-primary-tint text-bw-primary' : 'bg-bw-primary-tint text-bw-primary'
                  }`}>
                    {record.sectorType}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-surface-subtle text-bw-ink">
                    {record.contractType}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Agency:</span>
                    <span className="ml-2 text-sm text-bw-muted">{record.agency}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Value:</span>
                    <span className="ml-2 text-sm text-bw-muted">
                      ${record.contractValue > 1000000 ? `${(record.contractValue / 1000000).toFixed(1)}M` : `${(record.contractValue / 1000).toFixed(0)}K`}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Period:</span>
                    <span className="ml-2 text-sm text-bw-muted">
                      {record.startDate && record.endDate ? 
                        `${new Date(record.startDate).getFullYear()} - ${new Date(record.endDate).getFullYear()}` : 
                        'Date not specified'
                      }
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-6 mb-4">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-bw-ink mr-2">Rating:</span>
                    <StarRating rating={record.performanceRating} readOnly />
                  </div>
                  {record.onTimeDelivery && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success-bg text-success">
                      On-Time Delivery
                    </span>
                  )}
                  {record.withinBudget && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success-bg text-success">
                      Within Budget
                    </span>
                  )}
                </div>

                {record.deliverables && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-bw-ink">Deliverables:</span>
                    <p className="text-sm text-bw-muted mt-1">{record.deliverables}</p>
                  </div>
                )}

                {record.technologiesUsed && record.technologiesUsed.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Technologies:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {record.technologiesUsed.map((tech, techIndex) => (
                        <span key={techIndex} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-surface-subtle text-bw-ink">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingIndex(index)}
                  className="p-2 text-bw-muted hover:text-success"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteRecord(index)}
                  className="p-2 text-bw-muted hover:text-urgent"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {performances.length === 0 && !showAddForm && (
          <div className="text-center py-8 bg-bw-surface-subtle rounded-lg">
            <Award className="h-12 w-12 text-bw-muted mx-auto mb-3" />
            <p className="text-bw-muted">No performance records added yet</p>
            <div className="mt-3 flex justify-center space-x-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-bw-primary hover:text-bw-primary font-medium flex items-center"
              >
                <FileUp className="h-4 w-4 mr-1" />
                Import from document
              </button>
              <span className="text-bw-muted">or</span>
              <button
                onClick={() => setShowAddForm(true)}
                className="text-success hover:text-success font-medium"
              >
                Add manually
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Document Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-bw-surface-subtle0 bg-opacity-75 transition-opacity" onClick={() => !importing && setShowImportModal(false)} />
            <div className="relative inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-bw-surface shadow-xl rounded-lg">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b bg-bw-surface-subtle">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Sparkles className="h-6 w-6 text-bw-primary mr-2" />
                    <h3 className="text-lg font-semibold text-bw-ink">Import Past Performance</h3>
                  </div>
                  <button
                    onClick={() => !importing && setShowImportModal(false)}
                    className="text-bw-muted hover:text-bw-muted"
                    disabled={importing}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-1 text-sm text-bw-muted">
                  Review extracted past performance records and select which ones to import.
                </p>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                {importing ? (
                  <div className="flex flex-col items-center py-8">
                    <Loader2 className="h-12 w-12 text-bw-primary animate-spin mb-4" />
                    <p className="text-bw-muted">Analyzing document with AI...</p>
                    <p className="text-sm text-bw-muted mt-1">This may take a moment</p>
                  </div>
                ) : extractedProjects.length === 0 ? (
                  <div className="text-center py-8">
                    <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-bw-muted">No past performance records found in the document.</p>
                    <p className="text-sm text-bw-muted mt-2">Try uploading a capability statement, PPQ, or contract document.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-bw-muted">
                        Found {extractedProjects.length} record(s) - {selectedImports.size} selected
                      </span>
                      <button
                        onClick={() => {
                          if (selectedImports.size === extractedProjects.length) {
                            setSelectedImports(new Set());
                          } else {
                            setSelectedImports(new Set(extractedProjects.map((_, i) => i)));
                          }
                        }}
                        className="text-sm text-bw-primary hover:text-bw-primary"
                      >
                        {selectedImports.size === extractedProjects.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {extractedProjects.map((pp, index) => (
                      <div
                        key={index}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedImports.has(index) ? 'border-indigo-500 bg-bw-primary-tint' : 'border-bw-border hover:border-bw-border'
                        }`}
                        onClick={() => toggleImportSelection(index)}
                      >
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            checked={selectedImports.has(index)}
                            onChange={() => toggleImportSelection(index)}
                            className="h-4 w-4 text-bw-primary border-bw-border rounded mt-1 mr-3"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-bw-ink">{pp.projectName}</h4>
                              <span className={`text-xs px-2 py-1 rounded ${
                                pp.sectorType === 'Government' ? 'bg-bw-primary-tint text-bw-primary' : 'bg-bw-surface-subtle text-bw-ink'
                              }`}>
                                {pp.sectorType}
                              </span>
                            </div>
                            <div className="mt-1 text-sm text-bw-muted">
                              <span>{pp.agency}</span>
                              {pp.contractValue && (
                                <span className="ml-2">
                                  ${pp.contractValue.toLocaleString()}
                                </span>
                              )}
                              {pp.contractType && (
                                <span className="ml-2 text-bw-muted">({pp.contractType})</span>
                              )}
                            </div>
                            {pp.deliverables && (
                              <p className="mt-2 text-sm text-bw-muted line-clamp-2">{pp.deliverables}</p>
                            )}
                            {pp.technologiesUsed?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {pp.technologiesUsed.slice(0, 5).map((tech, i) => (
                                  <span key={i} className="text-xs px-2 py-0.5 bg-bw-surface-subtle text-bw-ink rounded">
                                    {tech}
                                  </span>
                                ))}
                                {pp.technologiesUsed.length > 5 && (
                                  <span className="text-xs text-bw-muted">+{pp.technologiesUsed.length - 5} more</span>
                                )}
                              </div>
                            )}
                            <div className="mt-2 text-xs text-bw-muted">
                              Confidence: {Math.round(pp.confidence * 100)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t bg-bw-surface-subtle flex justify-between items-center">
                <span className="text-sm text-bw-muted">
                  {selectedImports.size > 0 && `${selectedImports.size} record(s) will be imported`}
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowImportModal(false)}
                    disabled={importing}
                    className="px-4 py-2 border border-bw-border rounded-md text-bw-ink hover:bg-bw-surface-subtle disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportSelected}
                    disabled={loading || importing || selectedImports.size === 0}
                    className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Import Selected
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}